import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { adminCreateUser, adminListUsers, adminUnlockUser, adminDeleteUser } from "@/lib/admin-api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { Lock, Unlock, Trash2, RefreshCw, UserPlus, Copy } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/users")({
  component: UsersAdmin,
});

function generatePassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%^&*()-_=+[]{}";
  const all = upper + lower + digits + special;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  let pw = pick(upper) + pick(lower) + pick(digits) + pick(special);
  for (let i = 0; i < 10; i++) pw += pick(all);
  return pw.split("").sort(() => Math.random() - 0.5).join("");
}

function UsersAdmin() {

  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState<{ email: string; password: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminListUsers();
      setUsers(res.users);
    } catch (err: any) {
      toast.error(err.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !pw) return;
    setBusy(true);
    try {
      await adminCreateUser({ email, password: pw });
      toast.success("Utilisateur créé. Communiquez-lui le mot de passe : il devra le changer à la 1ère connexion.");
      setLastCreated({ email, password: pw });
      setEmail("");
      setPw(generatePassword());
      await load();
    } catch (err: any) {
      toast.error(err.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const onUnlock = async (id: string) => {
    try {
      await adminUnlockUser({ user_id: id });
      toast.success("Compte déverrouillé");
      await load();
    } catch (err: any) { toast.error(err.message || "Erreur"); }
  };

  const onDelete = async (id: string, email: string) => {
    if (!confirm(`Supprimer définitivement ${email} ?`)) return;
    try {
      await adminDeleteUser({ user_id: id });
      toast.success("Utilisateur supprimé");
      await load();
    } catch (err: any) { toast.error(err.message || "Erreur"); }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié");
  };

  const isLocked = (u: any) =>
    !!u.profile?.locked_at || (u.banned_until && new Date(u.banned_until).getTime() > Date.now());

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Utilisateurs</h1>
        <p className="text-sm text-muted-foreground">Création de comptes simples et déverrouillage après tentatives infructueuses.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base inline-flex items-center gap-2"><UserPlus className="h-4 w-4" /> Créer un utilisateur</CardTitle>
          <CardDescription>L'utilisateur devra changer ce mot de passe à sa première connexion.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onCreate} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] items-end">
            <div className="space-y-1.5">
              <Label htmlFor="em">Email</Label>
              <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw">Mot de passe initial</Label>
              <Input id="pw" type="text" value={pw} onChange={(e) => setPw(e.target.value)} required />
            </div>
            <Button type="button" variant="outline" onClick={() => setPw(generatePassword())}><RefreshCw className="h-4 w-4 mr-1" /> Générer</Button>
            <Button type="submit" disabled={busy}>{busy ? "…" : "Créer"}</Button>
          </form>
          {lastCreated && (
            <div className="mt-3 rounded-md border bg-amber-50 text-amber-900 p-3 text-sm space-y-1">
              <div className="font-semibold">Dernier utilisateur créé — copiez ces identifiants maintenant :</div>
              <div className="flex items-center gap-2"><span className="font-mono">{lastCreated.email}</span>
                <Button size="icon" variant="ghost" onClick={() => copy(lastCreated.email)}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="flex items-center gap-2"><span className="font-mono">{lastCreated.password}</span>
                <Button size="icon" variant="ghost" onClick={() => copy(lastCreated.password)}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? <p className="p-4 text-muted-foreground text-sm">Chargement…</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Tentatives</TableHead>
                  <TableHead>Dernière connexion</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => {
                  const locked = isLocked(u);
                  const mustChange = !!u.profile?.must_change_password;
                  return (
                    <TableRow key={u.id}>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell>
                        {u.is_admin
                          ? <Badge>admin</Badge>
                          : <Badge variant="secondary">utilisateur</Badge>}
                      </TableCell>
                      <TableCell>
                        {locked
                          ? <Badge variant="destructive" className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Verrouillé</Badge>
                          : mustChange
                            ? <Badge variant="outline">Mot de passe à changer</Badge>
                            : <Badge variant="secondary">Actif</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{u.profile?.failed_attempts ?? 0} / 3</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {u.last_sign_in_at ? fmtDateTime(u.last_sign_in_at) : "—"}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {locked && (
                          <Button size="sm" variant="outline" onClick={() => onUnlock(u.id)}>
                            <Unlock className="h-3.5 w-3.5 mr-1" /> Déverrouiller
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => onDelete(u.id, u.email)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
